import React, { useState, useRef } from 'react';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
import { Table, Spin, Pagination, Input, Button, Switch, Modal, message, Tag } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { useSearchBehavior } from '../hooks/useSearchBehavior';
import { GET_TAGS, UPDATE_TAG_STATUS } from '../mutations/tags';
import TagForm from '../components/tags/TagForm';
import TagAdvancedFilterModal from '../components/tags/TagAdvancedFilterModal';
import ActiveAdvancedFilters from '../components/tags/ActiveAdvancedFilters';

const Tags = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Usa o hook customizado para gerenciar o comportamento de busca
  const {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    handleManualSearch,
    clearSearch,
    isInstantSearch
  } = useSearchBehavior("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isAdvancedFilterModalVisible, setIsAdvancedFilterModalVisible] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);

  const [selectedTag, setSelectedTag] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const searchInputRef = useRef(null);
  const client = useApolloClient();
  const [updateTagStatus] = useMutation(UPDATE_TAG_STATUS);

  // Remove o useEffect do debounce, agora é gerenciado pelo hook customizado

  const { loading, error, data, refetch } = useQuery(GET_TAGS, {
    variables: { 
      page: currentPage, 
      perPage: pageSize, 
      search: activeSearchTerm, 
      activeOnly: !advancedFilters.some(filter => filter.field === 'active'),
      advancedFilters: advancedFilters.map(filter => ({
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
        fieldType: filter.fieldType
      }))
    },
    fetchPolicy: 'network-only', // Isso força sempre buscar do servidor
  });

  const handleSearch = () => {
    if (!isInstantSearch) {
      handleManualSearch();
    }
    setCurrentPage(1); // Reset page to 1 when search term changes
  };

  const showConfirmModal = (tag) => {
    setSelectedTag(tag);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setIsModalVisible(false);
    const { id, active } = selectedTag;
    try {
      await updateTagStatus({
        variables: { id, active: !active },
        update: (cache, { data: { updateTagStatus } }) => {
          const { tag } = updateTagStatus;
          cache.modify({
            id: cache.identify(tag),
            fields: {
              active(cachedActive) {
                return !active;
              }
            }
          });
        }
      });
    } catch (error) {
      console.error(error);
      message.error("Erro ao atualizar status da tag");
    }
  };

  const handleCancel = () => {
    setIsFormModalVisible(false);
    setIsModalVisible(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedTag(null);
    setIsEditMode(false);
  };

  const handleCreateTag = () => {
    resetForm();
    setIsFormModalVisible(true);
  };

  const handleEditTag = (tag) => {
    setIsEditMode(true);
    setSelectedTag(tag);
    setIsFormModalVisible(true);
  };

  const handleFormSuccess = (tag) => {
    setIsFormModalVisible(false);
    resetForm();
    
    // Invalida explicitamente o cache para a query GET_TAGS
    client.cache.evict({ fieldName: 'tags' });
    client.cache.gc();
    
    // Refaz a consulta para atualizar a lista
    refetch({
      page: currentPage,
      perPage: pageSize,
      search: activeSearchTerm,
      activeOnly: !advancedFilters.some(filter => filter.field === 'active'),
      advancedFilters: advancedFilters
    });
  };
  
  const showAdvancedFilterModal = () => {
    setIsAdvancedFilterModalVisible(true);
  };

  const handleAdvancedFilterCancel = () => {
    setIsAdvancedFilterModalVisible(false);
  };

  const handleAdvancedFilterApply = (filters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1); // Reset para primeira página quando aplicar filtros
    setIsAdvancedFilterModalVisible(false);
  };

  const getActiveAdvancedFiltersCount = () => {
    return advancedFilters.length;
  };
  
  const handleRemoveAdvancedFilter = (filterIndex) => {
    const newFilters = advancedFilters.filter((_, index) => index !== filterIndex);
    setAdvancedFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearAllAdvancedFilters = () => {
    setAdvancedFilters([]);
    setCurrentPage(1);
  };

  if (error) return <p>Error: {error.message}</p>;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag 
          color={record.backgroundColor || '#6fa8dc'} 
          style={{ 
            color: record.fontColor || '#000000',
            border: 'none',
            fontSize: '14px',
            padding: '4px 8px'
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: 'Cores',
      key: 'colors',
      align: 'center',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <div 
            style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: record.fontColor || '#000000',
              border: '1px solid #d9d9d9',
              borderRadius: '4px'
            }}
            title={`Cor da fonte: ${record.fontColor || '#000000 (padrão)'}`}
          />
          <div 
            style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: record.backgroundColor || '#6fa8dc',
              border: '1px solid #d9d9d9',
              borderRadius: '4px'
            }}
            title={`Cor de fundo: ${record.backgroundColor || '#6fa8dc (padrão)'}`}
          />
        </div>
      ),
    },
    {
      title: 'Clientes',
      dataIndex: 'customersCount',
      key: 'customersCount',
      align: 'center',
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: (text) => format(new Date(text), 'dd/MM/yyyy - HH:mm'),
    },
    {
      title: 'Ativo',
      dataIndex: 'active',
      key: 'active',
      align: 'center',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={() => showConfirmModal(record)}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (text, record) => (
        <Button type="link" onClick={() => handleEditTag(record)}>Editar</Button>
      ),
    },
  ];

  const handleTableChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const tags = data?.tags?.edges.map(edge => edge.node) || [];
  const { totalCount } = data?.tags || { totalCount: 0 };

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Input
          ref={searchInputRef}
          placeholder={isInstantSearch ? "Pesquisar tags (busca automática)" : "Pesquisar tags"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: 8, width: 300 }}
          onPressEnter={handleSearch}
          autoFocus
          allowClear={true}
        />
        {!isInstantSearch && (
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Pesquisar
          </Button>
        )}
        <Button
          type={getActiveAdvancedFiltersCount() > 0 ? "primary" : "default"}
          icon={<FilterOutlined />}
          onClick={showAdvancedFilterModal}
          style={{ marginLeft: 8 }}
        >
          Filtros Avançados
          {getActiveAdvancedFiltersCount() > 0 && (
            <span style={{ marginLeft: 4, backgroundColor: '#ff4d4f', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px' }}>
              {getActiveAdvancedFiltersCount()}
            </span>
          )}
        </Button>
        
        <Button type="primary" style={{ marginLeft: 'auto' }} onClick={handleCreateTag}>
          Criar Tag
        </Button>
      </div>
      <ActiveAdvancedFilters
        filters={advancedFilters}
        onRemoveFilter={handleRemoveAdvancedFilter}
        onClearAll={handleClearAllAdvancedFilters}
      />
      <Spin spinning={loading}>
        <Table
          dataSource={tags}
          columns={columns}
          rowKey="id"
          pagination={false}
        />
      </Spin>
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={totalCount}
        locale={{ items_per_page: '/ página' }}
        onChange={handleTableChange}
        showSizeChanger={true}
        style={{ marginTop: 16, textAlign: 'right' }}
      />
      <Modal
        title="Confirmação"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        getContainer={false}
      >
        <p>Tem certeza que deseja {selectedTag?.active ? 'desativar' : 'ativar'} esta tag?</p>
      </Modal>
      <Modal
        title={isEditMode ? "Editar Tag" : "Criar Tag"}
        open={isFormModalVisible}
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        getContainer={false}
      >
        <TagForm
          key={selectedTag?.id || 'new-tag'} 
          initialValues={selectedTag}
          onSuccess={handleFormSuccess}
          isEditMode={isEditMode}
        />
      </Modal>
      <TagAdvancedFilterModal
        open={isAdvancedFilterModalVisible}
        onCancel={handleAdvancedFilterCancel}
        onApply={handleAdvancedFilterApply}
        initialFilters={advancedFilters}
      />
    </>
  );
};

export default Tags;